import React, { useLayoutEffect, useRef, useState } from 'react';

// Lays out a section header (e.g. a CanvasBar with a title + right-aligned
// actions) above a "hugging" table/card, and constrains the header to the
// rendered width of that content — so the header's actions line up with the
// content's right edge instead of stretching to the full page width.
//
// Width source:
// - Default: the content slot is `width: max-content`, so it shrinks to the
//   table/card; we measure its rendered width and apply that exact pixel width
//   to the header. Being measured, it accounts for padding/scrollbars and
//   reacts to data/column changes.
// - `width` prop: for content that can't be measured after render — notably
//   react-virtualized, whose AutoSizer needs a concrete parent width up front —
//   pass an explicit width. Header and content both use it; no measuring runs.
//
// The header is a separate element from the measured content, so sizing it
// never feeds back into the measurement (no ResizeObserver loop).
export const HuggingSection = ({
  header,
  width,
  children
}: {
  header: React.ReactNode;
  width?: number;
  children: React.ReactNode;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (width != null) {
      return;
    }
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const measure = () => setMeasured(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  const resolvedWidth = width ?? measured;

  return (
    <>
      <div style={{ width: resolvedWidth, maxWidth: '100%' }}>{header}</div>
      <div
        ref={contentRef}
        style={{ width: width ?? 'max-content', maxWidth: '100%' }}
      >
        {children}
      </div>
    </>
  );
};

// Applied to a RowsRenderer's scroll container inside a HuggingSection so the
// container sizes to the (fitContent) table's content width — giving the
// content slot a definite width to measure. overflow-x stays hidden (from the
// component) so a vertical scrollbar can't spawn a horizontal one.
export const FIT_CONTENT_SCROLL_STYLE = {
  width: 'max-content',
  maxWidth: '100%'
} as const;
